import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { AirlineDropdownInput } from "flights/AirlineDropdownInput";
import { AirportDropdownInput } from "flights/AirportDropdownInput";
import { FlightDesignatorInput } from "flights/FlightDesignatorInput";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useCallback } from "react";
import { getAppointmentPhase } from "utilities";
import { useOpenSidePanel } from "../SidePanel";
import { Tabs } from "components/Tabs/Tabs";
import { FlightLookup } from "flights/lookup/FlightLookup";
import { useOpenFlightSidePanel } from "./FlightSidePanel";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { isFlightNumberOnly } from "shared/utils/flightUtils";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useClientId } from "common/SessionContext";

interface FlightNewInfo {
  artist: server.dto.ArtistSmall | null
  passengers: server.dto.ContactSmallV2[]
  phase: server.dto.AppointmentPhase

  travelAgency: string
  notes: string
  airline: server.dto.Airline
  bookingCode: string

  flightDesignator: string

  arrivalAirport: server.dto.Airport
  arrivalLocal: moment.Moment
  arrivalTerminal: string

  departureAirport: server.dto.Airport
  departureLocal: moment.Moment
  departureTerminal: string

  access: string
  capacity: number
  confirmationNumber: string
}

type NewFlightTabs = "lookup" | "commercial" | "privateJet";

interface Props {
  fromFlight?: server.dto.FlightSidePanelData
}

const NewFlightSidePanel = (props: Props) => {
  const planningContext = React.useContext(PlanningContext);
  const [selectedTab, setSelectedTab] = React.useState<NewFlightTabs>("lookup");
  const openFlightDetailSidePanel = useOpenFlightSidePanel();

  const [value, setValue] = React.useState<FlightNewInfo>({
    artist: props.fromFlight?.flight.artist ?? planningContext.opts?.artist ?? null,
    passengers: props.fromFlight?.passengers.map(p => p.contact) ?? [],
    phase: getAppointmentPhase(props.fromFlight?.flight.phase ?? "confirmed"),

    travelAgency: props.fromFlight?.flight.travelAgency ?? "",
    notes: props.fromFlight?.flight.notes ?? "",
    airline: props.fromFlight?.flight.airline ?? null,
    flightDesignator: "",
    bookingCode: "",

    arrivalAirport: null,
    arrivalLocal: props.fromFlight?.flight.dateTime.endDisplay ? moment.utc(props.fromFlight?.flight.dateTime.endDisplay) : (planningContext.opts?.date ?? moment.utc()),
    arrivalTerminal: "",

    departureAirport: null,
    departureLocal: props.fromFlight?.flight.dateTime.endDisplay ? moment.utc(props.fromFlight?.flight.dateTime.endDisplay) : (planningContext.opts?.date ?? moment.utc()),
    departureTerminal: "",

    access: "",
    capacity: null,
    confirmationNumber: props.fromFlight?.flight.confirmationNumber ?? "",
  });


  const fetchTravellersAndSaveArtist = async (artist: server.dto.ArtistSmall) => {

    const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
      companyId: artist.artistId,
      defaultSelect: true,
      isActive: true,
      page: 1,
      perPage: 1000,
    });

    if (result.data.hasErrors) {
      return;
    }

    onChange({
      artist,
      passengers: result.data.data.map(c => ({
        contactId: c.contact.contactId,
        culture: c.contact.culture,
        gender: c.contact.gender,
        isValidated: c.contact.isValidated,
        name: c.contact.name,
        position: c.contact.position,
        profileImageUrl: c.contact.profileImageUrl,
      }))
    });

  }

  React.useEffect(() => {
    if (value.artist && !props.fromFlight) {
      fetchTravellersAndSaveArtist(value.artist);
    }
  }, []);

  const clientId = useClientId();

  const onAirlineChange = (airline: server.dto.Airline) => {
    const isFlightDesignatorOnlyNumbers = isFlightNumberOnly(value.flightDesignator);
    if (isFlightDesignatorOnlyNumbers) {
      const airlineCode = airline.iata || airline.icao || "";
      onChange({ airline, flightDesignator: `${airlineCode}${value.flightDesignator}` });
    } else {
      onChange({ airline });
    }
  }

  const onChange = (newData: Partial<FlightNewInfo>) => {
    setValue({ ...value, ...newData });
  }

  const onFlightSelectFromLookup = (flight: server.dto.ScheduledFlightFromFlightStats) => {
    setSelectedTab("commercial");
    setValue({
      ...value,
      airline: flight.airline,
      flightDesignator: flight.flightDesignator,

      arrivalAirport: flight.arrivalAirport,
      arrivalLocal: moment.utc(flight.arrivalLocal),
      arrivalTerminal: flight.arrivalTerminal,

      departureAirport: flight.departureAirport,
      departureLocal: moment.utc(flight.departureLocal),
      departureTerminal: flight.departureTerminal,
    });
  }

  const submit = async () => {
    const instruction: server.dto.CreateFlightInstruction = {
      access: value.access,
      airlineId: value.airline?.airlineId || 0,
      arrivalAirportId: value.arrivalAirport.airportId,
      arrivalLocal: value.arrivalLocal.toDate(),
      arrivalTerminal: value.arrivalTerminal,
      artistId: value.artist?.artistId,
      clientId: clientId,
      passengers: value.passengers.map(p => {
        const passengerFromPreviousLeg = props.fromFlight?.passengers.find(passenger => passenger.contact.contactId === p.contactId);
        return {
          baggage: passengerFromPreviousLeg?.baggage ?? "",
          bookingReference: passengerFromPreviousLeg?.bookingReference ?? value.bookingCode,
          contact: p.contactId,
          flightId: 0,
          frequentFlyerNumber: "",
          frequentFlyerProgramId: null,
          meal: passengerFromPreviousLeg?.meal ?? "",
          seat: "",
          ticketNumber: passengerFromPreviousLeg?.ticketNumber ?? "",
          travelClass: passengerFromPreviousLeg?.travelClass ?? "unknown"
        }
      }),
      capacity: value.capacity ?? 0,
      confirmationNumber: value.confirmationNumber,
      departureAirportId: value.departureAirport.airportId,
      departureLocal: value.departureLocal.toDate(),
      departureTerminal: value.departureTerminal,
      flightDesignator: value.flightDesignator,
      flightType: selectedTab === "privateJet" ? "private" : "commercial",
      linkToShowId: planningContext.opts?.fromShowId || null,
      notes: value.notes,
      phase: value.phase.value,
      travelAgency: value.travelAgency,
    }

    const res = await window.CoreApi.Flight.CreateFlight(instruction);
    if (res.data.hasErrors) {
      return res;
    }

    if (props.fromFlight?.linkedShows.length > 0) {
      const linkPromises = props.fromFlight.linkedShows.map(show => {
        return window.CoreApi.Show.LinkFlight({
          flightId: res.data.data,
          showId: show.showId
        });
      });

      await Promise.all(linkPromises);
    }

    return res;
  }

  const queryClient = useQueryClient();

  return <SidePanelNewSectionTemplate
    saveDisabled={selectedTab === "lookup"}
    afterCreate="displayItemInSidebar"
    onSave={(id) => {
      invalidateFlightsCache(queryClient);
      openFlightDetailSidePanel(id);
    }}
    title={RESX.Flight.NewFlight}
    id="new-flight-side-panel"
    trigger={null}
    top={<Tabs
      options={[
        { label: RESX.Flight.FlightLookup, value: "lookup" },
        { label: RESX.Flight.CommercialFlight, value: "commercial" },
        { label: RESX.Flight.PrivateJet, value: "privateJet" },
      ]}
      value={selectedTab}
      onChange={v => setSelectedTab(v)}
    />}
    saveButtonLabel={RESX.GeneralLabels.NextResx}
    submit={submit}
    fields={[{
      key: "flightLookup",
      label: "",
      hideLabel: true,
      hidden: selectedTab !== "lookup",
      renderEditNew: () => <FlightLookup
        artist={value.artist}
        airline={value.airline}
        departureDate={value.departureLocal}
        onAirlineChange={airline => onChange({ airline })}
        onDepartureDateChange={departureDate => onChange({ departureLocal: departureDate })}
        onSelect={onFlightSelectFromLookup}
      />
    }, {
      key: "artist",
      label: site.currentEnvironment.functionGroups.artist.singular,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          canCreateNew={false}
          securityScope="limitByUserAccess"
          nonInPageEditing={{
            value: value.artist,
            onChange: artist => {
              if (artist) {
                fetchTravellersAndSaveArtist(artist);
              } else {
                onChange({ artist })
              }
            },
            validatorKey: "artist",
            required: true,
          }}
        />
      </div>
    }, {
      key: "passengers",
      label: RESX.Flight.Passengers,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          value={value.passengers}
          onChange={passengers => onChange({ passengers })}
          canCreateNew={false}
          required={false}
          validatorKey="contacts"
        />
      </div>
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <AppointmentPhaseDropdown
        value={value.phase}
        onChange={phase => onChange({ phase })}
      />
    }, {
      key: "travelAgency",
      label: RESX.Flight.TravelAgency,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.flight.maxTravelAgencyLength}
        value={value.travelAgency}
        onChange={travelAgency => onChange({ travelAgency })}
      />
    }, {
      key: "notes",
      label: RESX.Note.Notes,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <OptionalTextAreaInput
        maxLength={serverReference.constants.flight.maxNotesLength}
        rows={3}
        value={value.notes}
        onChange={notes => onChange({ notes })}
      />
    }, {
      key: "airline",
      hidden: selectedTab !== "commercial",
      label: RESX.Flight.Airline,
      renderEditNew: () => <AirlineDropdownInput
        value={value.airline}
        onChange={airline => onAirlineChange(airline)}
        isClearable={true}
        validatorKey="airline"
      />
    }, {
      key: "flightNumber",
      hidden: selectedTab !== "commercial",
      label: RESX.Flight.FlightNumber,
      renderEditNew: () => <FlightDesignatorInput
        placeholderAirline={value.airline?.iata || value.airline?.icao || ""}
        value={value.flightDesignator}
        validatorKey="flightNumber"
        onChange={flightDesignator => onChange({ flightDesignator })}
      />
    }, {
      key: "bookingCode",
      hidden: selectedTab !== "commercial" || props.fromFlight != null,
      label: RESX.Flight.BookingCode,
      renderEditNew: () => <OptionalTextInput
        value={value.bookingCode}
        onChange={bookingCode => onChange({ bookingCode })}
        maxLength={serverReference.constants.flight.maxBookingReferenceLength}
      />
    }, {
      key: "access",
      label: RESX.SoUser.Access,
      hidden: selectedTab !== "privateJet",
      renderEditNew: () => <OptionalTextAreaInput
        maxLength={serverReference.constants.flight.maxPrivateJetAccessLength}
        value={value.access}
        onChange={access => onChange({ access })}
      />
    }, {
      key: "confirmationNumber",
      label: RESX.Flight.ConfirmationNumber,
      hidden: selectedTab !== "privateJet",
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.flight.maxPrivateJetConfirmationNumberLength}
        value={value.confirmationNumber}
        onChange={confirmationNumber => onChange({ confirmationNumber })}
      />,
    }, {
      key: "capacity",
      label: RESX.Venue.Capacity,
      hidden: selectedTab !== "privateJet",
      renderEditNew: () => <OptionalNumberInput
        width={100}
        value={value.capacity}
        labelContent="PAX"
        onChange={capacity => onChange({ capacity })}
        maxValue={1000}
        canBeEmpty
        decimalPoints={0}
        onEnterPressed={value => onChange({ capacity: value })}
      />
    }, {
      key: "departureArrivalLabel",
      label: "",
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Departure}</h3>
        <h3 style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">{RESX.Flight.Arrival}</h3>
      </div>
    }, {
      key: "date",
      label: RESX.DateTime.lblDate,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <DateTimePicker
          value={value.departureLocal}
          onChange={departureLocal => {
            const diff = departureLocal.diff(value.departureLocal, "minutes");
            const newArrivalLocal = value.arrivalLocal.clone().add(diff, "minutes");
            onChange({ departureLocal, arrivalLocal: newArrivalLocal })
          }}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
        />
        <DateTimePicker
          value={value.arrivalLocal}
          onChange={arrivalLocal => onChange({ arrivalLocal })}
          validatorKey="arrivalDate"
          minDate={value.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={value.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
        />
      </div>
    }, {
      key: "airport",
      label: RESX.Flight.Airport,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <AirportDropdownInput
          value={value.departureAirport}
          onChange={departureAirport => onChange({ departureAirport })}
          validatorKey="departureAirport"
        />
        <AirportDropdownInput
          value={value.arrivalAirport}
          onChange={arrivalAirport => onChange({ arrivalAirport })}
          validatorKey="arrivalAirport"
        />
      </div>
    }, {
      key: "terminal",
      label: RESX.Flight.Terminal,
      hidden: selectedTab === "lookup",
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <OptionalTextInput
          width={60}
          value={value.departureTerminal}
          onChange={departureTerminal => onChange({ departureTerminal })}
          maxLength={serverReference.constants.flight.maxTerminalLength}
        />
        <OptionalTextInput
          width={60}
          value={value.arrivalTerminal}
          onChange={arrivalTerminal => onChange({ arrivalTerminal })}
          maxLength={serverReference.constants.flight.maxTerminalLength}
        />
      </div>
    }]}
  />
}

export const useOpenNewFlightSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewFlightSidePanel = useCallback(async (addingSegmentFromFlightId?: number) => {
    let fromFlight: server.dto.FlightSidePanelData = undefined;
    if (addingSegmentFromFlightId) {
      const res = await window.CoreApi.Flight.GetSidePanelData(addingSegmentFromFlightId);
      fromFlight = res.data.data;
    }
    openSidePanel(
      <NewFlightSidePanel fromFlight={fromFlight} />,
      "new-flight-side-panel",
      "large"
    );
  }, [openSidePanel]);

  return openNewFlightSidePanel;
}